import { userMessage } from "@/lib/errors";


export type Toast = {
  id: string;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
  kind: ToastKind;
  /** null = stays until dismissed */
  durationMs: number | null;
};

export type ToastKind = "success" | "error" | "info";

const DURATION: Record<ToastKind, number | null> = { success: 3000, info: 6000, error: null };
/** Toasts carrying an action (Undo, Open) stay long enough to reach it. */
const ACTION_MS = 8000;

type Listener = (toasts: Toast[]) => void;

let toasts: Toast[] = [];
let listeners: Listener[] = [];

export function subscribeToasts(listener: Listener) {
  listeners.push(listener);
  listener(toasts);
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

function emit() {
  for (const listener of listeners) {
    listener([...toasts]);
  }
}

export function showToast({
  message,
  actionLabel,
  onAction,
  kind = "success",
}: Pick<Toast, "message" | "actionLabel" | "onAction"> & { kind?: ToastKind }) {
  const id = Math.random().toString(36).substring(2, 9);
  const durationMs = kind !== "error" && actionLabel ? ACTION_MS : DURATION[kind];
  toasts = [{ id, message, actionLabel, onAction, kind, durationMs }, ...toasts].slice(0, 3);
  emit();
}

type Action = { actionLabel?: string; onAction?: () => void };
export const toast = {
  success: (message: string, a?: Action) => showToast({ message, ...a, kind: "success" }),
  info: (message: string, a?: Action) => showToast({ message, ...a, kind: "info" }),
  error: (message: string, a?: Action) => showToast({ message, ...a, kind: "error" }),
};

/** Error toast with the shared safe-message rules; persists until dismissed. */
export function toastError(err: unknown, fallback?: string) {
  toast.error(userMessage(err, fallback));
}

export function removeToast(id: string) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}
